import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import type { Route } from "./+types/get-started";
import { AuthShell } from "../components/layout";

export function meta({}: Route.MetaArgs) {
  const siteUrl = "https://schoolconferencego.com";
  return [
    { title: "Complete Setup | School Conference Go" },
    { name: "description", content: "Set up your school organization to start scheduling parent-teacher conferences with School Conference Go." },
    // Open Graph
    { property: "og:type", content: "website" },
    { property: "og:url", content: `${siteUrl}/get-started` },
    { property: "og:title", content: "Complete Setup | School Conference Go" },
    { property: "og:description", content: "Set up your school organization to start scheduling parent-teacher conferences with School Conference Go." },
    { property: "og:image", content: `${siteUrl}/og-image.jpg` },
    { property: "og:site_name", content: "School Conference Go" },
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Complete Setup | School Conference Go" },
    { name: "twitter:description", content: "Set up your school organization to start scheduling parent-teacher conferences." },
    { name: "twitter:image", content: `${siteUrl}/og-image.jpg` },
  ];
}

export default function GetStarted() {
  const navigate = useNavigate();
  const [schoolName, setSchoolName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<{ name?: string, email?: string } | null>(null);

  useEffect(() => {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("user");
      if (!token) {
          navigate("/login");
          return;
      }
      if (userData) {
          try {
              const parsedUser = JSON.parse(userData);
              setUser(parsedUser);
              if (parsedUser.email === "support@schoolconferencego.com") {
                  window.location.href = "/admin";
                  return;
              }
          } catch {
              localStorage.removeItem("user");
          }
      }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const token = localStorage.getItem("token");

    try {
      const res = await fetch("/api/organizations", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ schoolName }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Setup failed");
      }

      // Success! Update local storage with new token and context
      if (data.token) {
          localStorage.setItem("token", data.token);
      }
      if (data.context?.organizationId) {
          localStorage.setItem("organizationId", data.context.organizationId);
      }

      // Redirect to dashboard
      window.location.href = "/dashboard";
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={`Welcome, ${user?.name || "Teacher"}!`}
      subtitle="Let's set up your school organization to get started."
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="rounded-[var(--radius-md)] border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="schoolName" className="mb-1.5 block text-sm font-bold text-[var(--color-ink)]">
            School Name
          </label>
          <input
            id="schoolName"
            name="schoolName"
            type="text"
            required
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
            className="input-field"
            placeholder="e.g. Lincoln Elementary"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`btn btn-primary w-full ${loading ? "opacity-50" : ""}`}
        >
          {loading ? "Creating Organization..." : "Complete Setup"}
        </button>
      </form>
    </AuthShell>
  );
}

